import { FC, useEffect, useMemo } from 'react';

import { getGroupingsConfig, ADS_KEYS, injectRegistrationGroupsNode } from './config';
import { GroupingsProps } from './types';
import { trackEvent } from '../../../../../helpers/utils/analytics';
import { prepareTreeData, generateListKeys } from '../../../../../helpers/utils/reports';
import { groupingsAdminData } from '../../../constants/reportsWebinar';
import { useActions } from '../../../hooks/useActions';
import usePermission from '../../../hooks/usePermission';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import ModalSettings from '../ModalSettings';

/**
 * Универсальный компонент группировок для всех типов отчётов.
 * 
 * Заменяет:
 * - Groupings.tsx (universal)
 * - GroupingsOrder.tsx (order)
 * - GroupingsPayment.tsx (payment)
 * - GroupingsAD.tsx (ad)
 * - GroupingsWebinar.tsx (webinar)
 * 
 * Ключи генерируются на основе dataKey из констант:
 * - universal: reg_utm_source (без суффикса)
 * - order: reg_utm_source_2 (суффикс _2)
 * - payment: reg_utm_source_3 (суффикс _3)
 * - webinar: reg_utm_source_4 (суффикс _4)
 * - ad: ads_source (без суффикса)
 */
const ReportGroupings: FC<GroupingsProps> = ({
    reportType,
    isDisable,
    hidePersonalDataGroups,
    adsGroupsSelected,
    adsGroupsAvailable,
    setAdsGroupsAvailable,
    setAdsGroupsSelected,
    blockId,
}) => {
    const config = getGroupingsConfig(reportType);
    const isAdReport = reportType === 'ad';

    // Суффикс для dataKey динамически загружаемых доп. полей
    const extraFieldSuffixMap: Partial<Record<string, string>> = {
        order: '_2',
        payment: '_3',
        webinar: '_4',
        visit: '_6',
    };
    const extraFieldSuffix = extraFieldSuffixMap[reportType];
    const checkPermission = usePermission();
    
    // Для блоков дашборда читаем из блочного state, иначе из глобального
    const { groupings } = useTypedSelector((state) => {
        if (blockId) {
            return state.table.blocks[blockId] ?? state.table;
        }
        return state.table;
    });
    // «Группы регистраций» всегда глобальные (не блочные), как и createdMetrics
    const customGroups = useTypedSelector((state) => state.table.customGroups);
    // Справочник «Параметров Яндекс.Директ» — тоже глобальный; в дерево попадает только в отчёте рекламы (config case 'ad')
    const utmSections = useTypedSelector((state) => state.table.utmSections);
    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
    const {
        setGroupings,
        setMetaTableFetching,
        setMetaChartFetching,
        setMetaCompareChartFetching,
        setMetaCompareTableFetching,
        getCustomGroups,
        getUtmSections,
    } = useActions();

    // Подгружаем список групп для отчётов, где встроен узел «Группы регистраций»
    // (universal + реклама + заказы + платежи + вебинары: reg-поля в неймспейсе регистраций). Кэш TTL 30с в unApi.
    useEffect(() => {
        const hasGroupsNode = reportType === 'universal' || reportType === 'ad'
            || reportType === 'order' || reportType === 'payment' || reportType === 'webinar';
        if (hasGroupsNode && selectedSchool?.id) {
            getCustomGroups(selectedSchool.id, window.location.pathname, undefined, false);
        }
        // «Параметры Яндекс.Директ» — только в отчёте рекламы. Кэш TTL 30с в unApi.
        if (reportType === 'ad' && selectedSchool?.id) {
            getUtmSections(selectedSchool.id, undefined, false);
        }
    }, [ reportType, selectedSchool?.id ]);

    // Данные дерева — ключи стабильны (key = dataKey). customGroups встраиваются в узел «Группы регистраций»
    const data = useMemo(() => {
        // Для webinar отчёта superadmin видит дополнительные группировки.
        // Узел «Группы регистраций» встраиваем и в это дерево (как в config.getData).
        if (reportType === 'webinar' && checkPermission([ 'superadmin' ])) {
            return prepareTreeData(
                injectRegistrationGroupsNode(JSON.parse(JSON.stringify(groupingsAdminData)), customGroups)
            );
        }
        return config.getData(customGroups, utmSections);
    }, [ config, reportType, checkPermission, customGroups, utmSections ]);

    const listKeys = useMemo(() => {
        // Для webinar отчёта superadmin видит дополнительные группировки.
        if (reportType === 'webinar' && checkPermission([ 'superadmin' ])) {
            return generateListKeys(prepareTreeData(
                injectRegistrationGroupsNode(JSON.parse(JSON.stringify(groupingsAdminData)), customGroups)
            ));
        }
        return config.getListKeys(customGroups, utmSections);
    }, [ config, reportType, checkPermission, customGroups, utmSections ]);

    const actionSet = (selected: any) => {
        // Для блоков дашборда передаём blockId в action
        setGroupings({ data: selected, blockId });

        trackEvent('report-groupings-apply', {
            count: (selected ?? []).length,
            groupings: (selected ?? []).map((item: any) => item?.dataKey).filter(Boolean).slice(0, 50),
            report_type: reportType,
        });

        setTimeout(() => {
            setMetaTableFetching({ value: true, blockId });
            setMetaChartFetching({ value: true, blockId });
            setMetaCompareChartFetching({ value: true });
            setMetaCompareTableFetching({ value: true });
        }, 50);
    };

    // Формируем defChecked: используем dataKey, так как ключи в дереве = dataKey
    // Это обеспечивает совместимость со старыми сохранёнными отчётами, где key мог быть в формате "0-0-0"
    const defChecked = useMemo(() => {
        return (groupings ?? []).filter(Boolean).map((item: any) => item?.dataKey).filter(Boolean);
    }, [ groupings ]);

    return (
        <span data-help-id="report-groupings">
        <ModalSettings
            title="Группировки"
            dataItems={data}
            listKeys={listKeys}
            defSelected={groupings ?? []}
            defChecked={defChecked}
            isDisable={isDisable}
            actionSet={actionSet}
            hidePersonalDataGroups={hidePersonalDataGroups}
            adsGroupsSelected={config.hasAdsGroups ? adsGroupsSelected : undefined}
            adsGroupsAvailable={config.hasAdsGroups ? adsGroupsAvailable : undefined}
            setAdsGroupsAvailable={config.hasAdsGroups ? setAdsGroupsAvailable : undefined}
            setAdsGroupsSelected={config.hasAdsGroups ? setAdsGroupsSelected : undefined}
            isAdReport={isAdReport}
            extraFieldSuffix={extraFieldSuffix}
        />
        </span>
    );
};

export default ReportGroupings;

// Экспорт ads_keys для обратной совместимости
export const ads_keys = ADS_KEYS;
